import { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

// loginRedirect: what src/googleSignIn.js took from the URL before render,
// { loginCode, error }
export const AuthProvider = ({ children, loginRedirect = {} }) => {
  const [staff, setStaff] = useState(() => {
    try { return JSON.parse(localStorage.getItem('staff')); } catch { return null; }
  });
  const [signingIn, setSigningIn] = useState(Boolean(loginRedirect.loginCode));
  const [signInError, setSignInError] = useState(loginRedirect.error || null);
  const exchanged = useRef(false);

  // Trade the single-use login code for a session, exactly once (StrictMode
  // runs effects twice in development; the code would be spent by the first)
  const loginCode = loginRedirect.loginCode;
  useEffect(() => {
    if (!loginCode || exchanged.current) return;
    exchanged.current = true;
    api.post('/auth/google/exchange', { code: loginCode })
      .then(({ data }) => {
        // Stored where the password login always kept the session: api.js
        // sends `token`, and this provider restores `staff` on reload
        localStorage.setItem('token', data.token);
        localStorage.setItem('staff', JSON.stringify(data.staff));
        setStaff(data.staff);
        setSignInError(null);
      })
      .catch((err) => {
        setSignInError(err.response?.status === 429 ? 'rate_limited' : (err.response?.data?.reason || 'server_error'));
      })
      .finally(() => setSigningIn(false));
  }, [loginCode]);

  // Password fallback during the Google rollout — same storage as the
  // code-exchange path above
  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('staff', JSON.stringify(data.staff));
    setStaff(data.staff);
    return data.staff;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('staff');
    setStaff(null);
  };

  return (
    <AuthContext.Provider value={{ staff, signingIn, signInError, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
